import React from 'react';
import { flexRender } from '@tanstack/react-table';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Table } from 'react-bootstrap';

import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';

const getColumnAlignment = column => {
  const valueType = column.columnDef.meta?.valueType;

  if (valueType === 'number') return 'text-end';
  if (valueType === 'string') return 'text-start';

  const firstRow = column.getFacetedRowModel().rows[0];
  if (firstRow) {
    const value = firstRow.getValue(column.id);
    if (typeof value === 'number') return 'text-end';
  }

  return 'text-start';
};

const TableBody = ({
  headerClassName,
  bodyClassName,
  rowClassName,
  tableProps,
  renderExpandedRow,
}) => {
  const table = useAdvanceTableContext();
  const { getRowModel, getFlatHeaders } = table;

  return (
    <div className="table-responsive scrollbar">
      <Table {...tableProps}>
        <thead className={headerClassName}>
          <tr>
            {getFlatHeaders().map(header => {
              const alignment = getColumnAlignment(header.column);

              return (
                <th
                  key={header.id}
                  {...header.column.columnDef.meta?.headerProps}
                  className={classNames(
                    'fs-10',
                    'text-900',
                    alignment,
                    header.column.columnDef.meta?.headerProps?.className,
                    {
                      sort: header.column.getCanSort(),
                      desc: header.column.getIsSorted() === 'desc',
                      asc: header.column.getIsSorted() === 'asc',
                    },
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{
                    ...header.column.columnDef.meta?.headerProps?.style,
                    minWidth: header.column.columnDef.meta?.minWidth,
                    maxWidth: header.column.columnDef.meta?.maxWidth,
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className={bodyClassName}>
          {getRowModel().rows.map(row => (
            <React.Fragment key={row.id}>
              <tr className={classNames(rowClassName)}>
                {row.getVisibleCells().map(cell => {
                  const alignment = getColumnAlignment(cell.column);

                  return (
                    <td
                      key={cell.id}
                      {...cell.column.columnDef.meta?.cellProps}
                      className={classNames(
                        'align-middle',
                        alignment,
                        cell.column.columnDef.meta?.cellProps?.className,
                      )}
                      style={{
                        ...cell.column.columnDef.meta?.cellProps?.style,
                        minWidth: cell.column.columnDef.meta?.minWidth,
                        maxWidth: cell.column.columnDef.meta?.maxWidth,
                      }}
                    >
                      <div
                        className={classNames({
                          'text-truncate': cell.column.columnDef.meta?.truncate,
                        })}
                        title={
                          cell.column.columnDef.meta?.truncate
                            ? cell.getValue()
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
              {row.getIsExpanded() && renderExpandedRow && (
                <tr className="bg-light">
                  <td colSpan={row.getVisibleCells().length} className="p-0">
                    {renderExpandedRow(row)}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

TableBody.propTypes = {
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  rowClassName: PropTypes.string,
  tableProps: PropTypes.object,
  renderExpandedRow: PropTypes.func,
};

export default TableBody;
